import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ActionRequestRead,
  ActionStatus,
  ActionType,
  MessageRead,
  MessageSender,
  QueryRequest,
  SessionRead,
  StreamEvent,
  StreamEventType,
  SystemResponse,
  Task,
  WorkflowPlan,
} from '../julia-agent-openapi/models';
import { ActionRequestsService, ChatService, MessageService, SessionService } from '../julia-agent-openapi/services';
import { environment } from '../../../../environments/environment';
import { Block, BLOCK_EVENT } from '../julia-blocks/julia-block.model';
import { MessageBlocks } from '../julia-blocks/message-blocks';

import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

// Extended interface to track event types for styling
interface ExtendedMessageRead extends MessageRead {
  event_type?: StreamEventType;
  blocks?: Block[];
  blocksExpired?: boolean;
}

@Component({
  selector: 'app-julia-chat',
  templateUrl: './julia-chat.component.html',
  styleUrls: ['./julia-chat.component.scss'],
})
export class JuliaChatComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  isChatOpen = false;
  newMessage = '';
  logoJulia = environment.juliaLogo;
  sessions: SessionRead[] = [];
  selectedSession: SessionRead | null = null;
  messages: ExtendedMessageRead[] = [];
  temporaryMessages: ExtendedMessageRead[] = []; // For transient events like PROGRESS, TASK_PROGRESS
  currentTasks: Task[] = []; // Workflow tasks from workflow_plan
  // Every pending action request, any type. The wording comes from action_type.
  pendingActionRequests: ActionRequestRead[] = [];

  loadingSessions = true;
  loadingMessages = false;

  loadingAssistant = false;

  searchOpened = false;
  
  private currentSubscription?: Subscription;
  // Safety net: a backend that dies without closing fires neither callback.
  private inactivityTimer?: ReturnType<typeof setTimeout>;
  showPreviewModal = false;
  selectedActionRequestForPreview: ActionRequestRead | null = null;
 

  constructor(
    private juliaSessionService: SessionService,
    private juliaMessageService: MessageService,
    private juliaChatService: ChatService,
    private juliaActionRequestsService: ActionRequestsService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.loadSessions();
  }

  toggleChat() {
    this.isChatOpen = !this.isChatOpen;
  }

  loadSessions() {
    this.loadingSessions = true;
    this.juliaSessionService.getSessionsApiV1SessionGet().subscribe({
      next: (res) => {
        this.sessions = res;
        this.loadingSessions = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erreur sessions:', err);
        this.loadingSessions = false;
      },
    });
  }

  createNewSession(loadMessages =true): Promise<SessionRead> {
  return new Promise((resolve, reject) => {
    this.juliaSessionService.createSessionApiV1SessionPost().subscribe({
      next: (session) => {
        this.sessions.unshift(session);
        this.selectSession(session, loadMessages);
        resolve(session);
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erreur création session:', err);
        reject(err);
      },
    });
  });
}

  toggleSessionDropdown() {
    this.searchOpened = !this.searchOpened;
  }

  selectSession(session: SessionRead , loadMessages = true) {
    this.selectedSession = session;
    this.searchOpened = false; // fermer le menu après sélection
    if (loadMessages) {
      this.loadMessages(session.id!);
    }
  }

  loadMessages(sessionId: string) {
    this.loadingMessages = true;
    this.juliaMessageService
      .getMessagesApiV1MessageSessionSessionIdGet({ session_id: sessionId })
      .subscribe({
        next: (msgs) => {
          this.messages = msgs;
          this.loadingMessages = false;
          this.scrollToBottom();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Erreur chargement messages:', err);
          this.loadingMessages = false;
        },
      });
  }

  async sendMessage() {
    if (!this.newMessage.trim() ) return;
   if (!this.selectedSession) {
    try {
      const session = await this.createNewSession(false);
      this.selectedSession = session;
    } catch (err) {
      console.error('Session creation failed', err);
      return;
    }
  }
  
    if(!this.selectedSession) return;

    const userMessage: MessageRead = {
      id: uuidv4(),
      session_id: this.selectedSession.id!,
      content: this.newMessage,
      type: MessageSender.User,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      meta: null,
    };

    this.messages = [...this.messages, userMessage];
    
    const request: QueryRequest = {
      query: this.newMessage,
      session_id: this.selectedSession.id!,
    };

    this.newMessage = '';
    this.scrollToBottom();

    this.loadingAssistant = true;
    this.armInactivityTimer();
    const mainMessageId = uuidv4();
    const progressIds: string[] = [];

    this.currentSubscription = this.juliaChatService.streamQueryApiV1ChatQueryStreamPost({ body: request })
      .subscribe({
        next: (event: StreamEvent) => {
          // Any frame, HEARTBEAT included, proves the stream is alive.
          this.armInactivityTimer();

          if ((event.event_type as string) === BLOCK_EVENT) {
            this.addBlock(mainMessageId, (event.data as { block?: Block }).block);
            this.scrollToBottom();
            return;
          }

          switch (event.event_type) {
            case StreamEventType.HEARTBEAT:
              // Nothing to render; re-arming the timer above was the point.
              break;

            case StreamEventType.PROGRESS:
              {
                // Clear previous progress messages and add new one temporarily
                this.temporaryMessages = [{
                  id: uuidv4(),
                  session_id: this.selectedSession!.id!,
                  content: event.data.content ?? 'Processing...',
                  type: MessageSender.Assistant,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  meta: null,
                  event_type: StreamEventType.PROGRESS,
                }];
                break;
              }
            case StreamEventType.CHUNK:
              {
                // Clear temporary messages when actual content arrives
                this.temporaryMessages = [];

                const mainMessage = this.messages.find(msg => msg.id === mainMessageId);
                if (mainMessage) {
                  mainMessage.content= event.data.content ?? '';
                  mainMessage.updated_at = new Date().toISOString();
                  mainMessage.event_type = StreamEventType.CHUNK;
                  this.messages = [...this.messages];
                }else{
                  const assistantMsg: ExtendedMessageRead = {
                    id: mainMessageId,
                    session_id: this.selectedSession!.id!,
                    content: event.data.content ?? '(vide)',
                    type: MessageSender.Assistant,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    meta: null,
                    event_type: StreamEventType.CHUNK,
                  };
                  this.messages = [...this.messages, assistantMsg];
                }
                
                break;

              }
              
            case StreamEventType.FINAL_RESULT:
              {
                // Clear temporary messages when final result arrives
                this.temporaryMessages = [];
      
                const mainMessage = this.messages.find(msg => msg.id === mainMessageId);
                if (mainMessage) {
                  mainMessage.content= event.data.content ?? '';
                  mainMessage.updated_at = new Date().toISOString();
                  mainMessage.event_type = StreamEventType.FINAL_RESULT;
                  this.messages = [...this.messages];
                }else{
                  const assistantMsg: ExtendedMessageRead = {
                    id: mainMessageId,
                    session_id: this.selectedSession!.id!,
                    content: event.data.content ?? '(vide)',
                    type: MessageSender.Assistant,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    meta: null,
                    event_type: StreamEventType.FINAL_RESULT,
                  };
                  this.messages = [...this.messages, assistantMsg];
                }
                break;
              }
            case StreamEventType.TASKS:
              {
                
                // Extract workflow plan and merge tasks intelligently
                if (event.data.workflow_plan && event.data.workflow_plan.tasks) {
                  const newTasks = event.data.workflow_plan.tasks;
                  
                  // Create a map of existing tasks by ID for quick lookup
                  const existingTasksMap = new Map(
                    this.currentTasks.map(task => [task.id, task])
                  );
                  
                  // Merge: keep existing task state if task ID exists, otherwise add new task
                  this.currentTasks = newTasks.map(newTask => {
                    const existingTask = existingTasksMap.get(newTask.id);
                    
                    if (existingTask) {
                      // Keep the frontend-managed state (status, completed_at, result)
                      return {
                        ...newTask,
                        status: existingTask.status,
                        completed_at: existingTask.completed_at,
                        result: existingTask.result,
                      };
                    }
                    
                    // New task, use as-is
                    return newTask;
                  });
                  
                }
                break;
              }
            case StreamEventType.TASK_START:
              {
                // Update task status to in_progress
                if (event.data.task_id) {
                  const task = this.currentTasks.find(t => t.id === event.data.task_id);
                  if (task) {
                    task.status = 'in_progress';
                    this.currentTasks = [...this.currentTasks];
                  }
                }
                break;
              }
            case StreamEventType.TASK_PROGRESS:
              {
                // Clear previous progress and add new one temporarily
                this.temporaryMessages = [{
                  id: uuidv4(),
                  session_id: this.selectedSession!.id!,
                  content: event.data.task_step || event.data.content || 'Task in progress...',
                  type: MessageSender.Assistant,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  meta: null,
                  event_type: StreamEventType.TASK_PROGRESS,
                }];
                break;
              }
            case StreamEventType.TASK_OUTPUT:
              {
                // Update task with output/result
                if (event.data.task_id && event.data.task_output) {
                  const task = this.currentTasks.find(t => t.id === event.data.task_id);
                  if (task) {
                    task.result = event.data.task_output;
                    this.currentTasks = [...this.currentTasks];
                  }
                }
                break;
              }
            case StreamEventType.TASK_COMPLETE:
              {
                // Update task status to completed
                if (event.data.task_id) {
                  const task = this.currentTasks.find(t => t.id === event.data.task_id);
                  if (task) {
                    task.status = 'completed';
                    task.completed_at = new Date().toISOString();
                    this.currentTasks = [...this.currentTasks];
                  }
                }
                
                break;
              }
            case StreamEventType.TASK_ERROR:
              {
                // Update task status to failed
                if (event.data.task_id) {
                  const task = this.currentTasks.find(t => t.id === event.data.task_id);
                  if (task) {
                    task.status = 'failed';
                    this.currentTasks = [...this.currentTasks];
                  }
                }
                
                // const taskErrorMsg: ExtendedMessageRead = {
                //   id: uuidv4(),
                //   session_id: this.selectedSession!.id!,
                //   content: event.data.task_error || event.data.content || 'Task error',
                //   type: MessageSender.Assistant,
                //   created_at: new Date().toISOString(),
                //   updated_at: new Date().toISOString(),
                //   meta: null,
                //   event_type: StreamEventType.TASK_ERROR,
                // };
                // this.messages = [...this.messages, taskErrorMsg];
                break;
              }
            case StreamEventType.AGENT_SUMMARY:
              {
                const agentSummaryMsg: ExtendedMessageRead = {
                  id: uuidv4(),
                  session_id: this.selectedSession!.id!,
                  content: event.data.agent_summary || event.data.content || 'Agent summary',
                  type: MessageSender.Assistant,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  meta: null,
                  event_type: StreamEventType.AGENT_SUMMARY,
                };
                this.messages = [...this.messages, agentSummaryMsg];
                break;
              }
            case StreamEventType.COMPLETE:
              {
                // Clear all temporary messages on completion
                this.temporaryMessages = [];
                // Clear tasks after completion
                this.currentTasks = [];
                // Clear pending action requests
                this.pendingActionRequests = [];
                
                const streamSession = event.data.session;
                this.selectedSession = streamSession || this.selectedSession;
                
                this.sessions = this.sessions.map(sess => {
                  if(sess.id === this.selectedSession!.id){
                    return streamSession ||sess ; 
                  }
                  return sess;
                });
                this.selectedSession = streamSession || this.selectedSession;
                
                break;
              }

            case StreamEventType.CONTINUATION_REQUIRED:
              {
                // An ActionRequest like any other; only the wording differs.
                this.temporaryMessages = [];
                if (event.data.action_request) {
                  this.pendingActionRequests.push(event.data.action_request);
                }
                break;
              }

            case StreamEventType.TIMEOUT:
              {
                // Terminal frame: cancelled for running too long, or stopped.
                this.showAssistantError(
                  'julia.errors.timeout',
                  event.data.content ?? undefined
                );
                break;
              }

            case StreamEventType.ERROR:
            case StreamEventType.SYSTEM_ERROR:
              {
                // Previously swallowed by `default` — nothing was shown.
                this.showAssistantError(
                  'julia.errors.generic',
                  event.data.content || event.data.system_error || undefined
                );
                break;
              }

            case StreamEventType.AUTHORIZATION_REQUIRED:
              {
                console.log("Auth required event received:", event);
                if (event.data.action_request) {
                  // Store the action request for user approval
                  const actionRequest = event.data.action_request as ActionRequestRead;
                  this.pendingActionRequests.push(actionRequest);
                }
                break;
              }
                            
            default:
              break;
          }

          this.scrollToBottom();
         
        },
        error: (err) => {
          console.error('Stream error:', err);
          this.clearInactivityTimer();
          this.temporaryMessages = [];
          this.loadingAssistant = false;
          this.currentSubscription = undefined;
        },
        complete: () => {
          this.clearInactivityTimer();
          this.temporaryMessages = [];
          this.loadingAssistant = false;
          this.currentSubscription = undefined;
        }
      });
  }

  /** A block shown after the answer: kept on the answer's message, which then shows it instead of its markdown. */
  private addBlock(messageId: string, block?: Block) {
    const message = this.messages.find(msg => msg.id === messageId);
    if (!message || !block) return;
    message.blocks = [...(message.blocks ?? []), block];
    this.messages = [...this.messages];
  }

  messageText(msg: ExtendedMessageRead): string {
    return MessageBlocks.text(msg);
  }

  messageBlocks(msg: ExtendedMessageRead): Block[] {
    return MessageBlocks.blocks(msg);
  }

  /** A block's stored result is gone: the message shows its markdown again. */
  blocksExpired(msg: ExtendedMessageRead) {
    msg.blocksExpired = true;
    this.messages = [...this.messages];
  }

  trackBlock(_: number, block: Block): string {
    return block.id;
  }

  /** Restart the no-activity countdown, on every frame received. */
  private armInactivityTimer() {
    this.clearInactivityTimer();
    this.inactivityTimer = setTimeout(
      () => this.onStreamStalled(),
      environment.juliaStreamInactivityMs
    );
  }

  private clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = undefined;
    }
  }

  /** No keepalive either: assume dead. Unsubscribing closes the request, which
   * the backend reads as a disconnect. */
  private onStreamStalled() {
    if (!this.currentSubscription) return;
    console.warn('Julia stream stalled, giving up');
    this.showAssistantError('julia.errors.stream_stalled');
    this.cancelRequest();
  }

  /** Surface a failure as a message and take the UI out of its loading state. */
  private showAssistantError(translationKey: string, content?: string) {
    this.temporaryMessages = [];
    this.currentTasks = [];
    this.pendingActionRequests = [];
    this.messages = [
      ...this.messages,
      {
        id: uuidv4(),
        session_id: this.selectedSession?.id ?? '',
        content: content || this.translate.instant(translationKey),
        type: MessageSender.Assistant,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        meta: null,
        event_type: StreamEventType.ERROR,
      },
    ];
    this.loadingAssistant = false;
    this.scrollToBottom();
  }

  /** Same object on the wire, different question: only the labels branch. */
  private isContinuation(actionRequest: ActionRequestRead): boolean {
    return actionRequest.action_type === ActionType.ContinueExecution;
  }

  actionIcon(actionRequest: ActionRequestRead): string {
    return this.isContinuation(actionRequest) ? '⏳' : '🔐';
  }

  headerKey(actionRequest: ActionRequestRead): string {
    return this.isContinuation(actionRequest)
      ? 'julia.continuation.title'
      : 'julia.action_request.authorization_required';
  }

  approveKey(actionRequest: ActionRequestRead): string {
    return this.isContinuation(actionRequest)
      ? 'julia.continuation.continue'
      : 'julia.action_request.approve';
  }

  denyKey(actionRequest: ActionRequestRead): string {
    return this.isContinuation(actionRequest)
      ? 'julia.continuation.stop'
      : 'julia.action_request.deny';
  }

  /** A continuation prompt carries a localised sentence from the backend. */
  actionMessage(actionRequest: ActionRequestRead): string {
    if (this.isContinuation(actionRequest)) {
      const message = actionRequest.preview_data?.['message'];
      return typeof message === 'string' && message
        ? message
        : this.translate.instant('julia.continuation.default_message');
    }
    return this.translate.instant(
      actionRequest.action_type
        ? 'julia.action_type.' + actionRequest.action_type
        : 'julia.action_request.default_message'
    );
  }

  cancelRequest() {
    if (this.currentSubscription) {
      // Aborts the HTTP request; the backend reads that as a disconnect.
      this.currentSubscription.unsubscribe();
      this.currentSubscription = undefined;
      this.clearInactivityTimer();
      this.loadingAssistant = false;
      this.temporaryMessages = [];
      this.currentTasks = [];
      this.pendingActionRequests = [];
    }
  }

  ngOnDestroy() {
    this.clearInactivityTimer();
    this.currentSubscription?.unsubscribe();
  }
   isSubscriptionActive(): boolean {
    return this.currentSubscription !== undefined;
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop =
          this.scrollContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }

  isMessageSentByUser(msg: ExtendedMessageRead) {
    return msg.type === 'user';
  }

  getMessageEventClass(msg: ExtendedMessageRead): string {
    if (!msg.event_type) return '';
    
    switch (msg.event_type) {
      case StreamEventType.PROGRESS:
        return 'event-progress';
      case StreamEventType.CHUNK:
        return 'event-chunk';
      case StreamEventType.FINAL_RESULT:
        return 'event-final-result';
      case StreamEventType.TASK_START:
        return 'event-task-start';
      case StreamEventType.TASK_PROGRESS:
        return 'event-task-progress';
      case StreamEventType.TASK_COMPLETE:
        return 'event-task-complete';
      case StreamEventType.TASK_ERROR:
        return 'event-task-error';
      case StreamEventType.AGENT_SUMMARY:
        return 'event-agent-summary';
      case StreamEventType.MESSAGE_START:
        return 'event-message-start';
      case StreamEventType.MESSAGE_DELTA:
        return 'event-message-delta';
      case StreamEventType.MESSAGE_END:
        return 'event-message-end';
      default:
        return '';
    }
  }

  getTaskStatusClass(task: Task): string {
    switch (task.status) {
      case 'completed':
        return 'completed';
      default:
        return '';
    }
  }

  approveActionRequest(actionRequest: ActionRequestRead) {
    this.juliaActionRequestsService
      .resolveActionRequestApiV1ActionRequestsRequestIdResolvePost({
        request_id: actionRequest.id,
        body: {
          approved: true,
          denial_reason: null,
        },
      })
      .subscribe({
        next: (resolvedRequest) => {
          console.log('Action request approved:', resolvedRequest);
          // Remove from pending list
          this.pendingActionRequests = this.pendingActionRequests.filter(
            req => req.id !== actionRequest.id
          );
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error approving action request:', err);
        },
      });
  }

  denyActionRequest(actionRequest: ActionRequestRead, reason: string = 'Denied by user') {
    this.juliaActionRequestsService
      .resolveActionRequestApiV1ActionRequestsRequestIdResolvePost({
        request_id: actionRequest.id,
        body: {
          approved: false,
          denial_reason: reason,
        },
      })
      .subscribe({
        next: (resolvedRequest) => {
          console.log('Action request denied:', resolvedRequest);
          // Remove from pending list
          this.pendingActionRequests = this.pendingActionRequests.filter(
            req => req.id !== actionRequest.id
          );
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error denying action request:', err);
        },
      });
  }

  openPreviewModal(actionRequest: ActionRequestRead) {
    console.log('Opening preview modal with action request:', actionRequest);
    this.selectedActionRequestForPreview = actionRequest;
    this.showPreviewModal = true;
  }

  closePreviewModal() {
    this.showPreviewModal = false;
    this.selectedActionRequestForPreview = null;
  }

  onModalAction(result: { action: string }) {
    if (this.selectedActionRequestForPreview) {
      if (result.action === 'approve') {
        this.approveActionRequest(this.selectedActionRequestForPreview);
      } else if (result.action === 'deny') {
        this.denyActionRequest(this.selectedActionRequestForPreview);
      }
    }
    this.closePreviewModal();
  }

}
